// LeadsListPage — список лидов (V12 U-34-E): таблица (Имя/Компания/Email/Телефон/Лицензия/Сайт/IP/
// Источник/Статус/Действия), фильтр статуса (чипы), живой поиск q+fields (SearchFieldBar, R63),
// создание (диалог) и конверсия (LeadConvertDialog → navigate к сделке + invalidate).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Chip,
  Fab,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { leadsApi, type Lead, type LeadStatus } from '@/api/leads';
import type { Deal } from '@/api/deals';
import { SearchFieldBar, type SearchFieldOption, type SearchFieldValue } from '@/components/SearchFieldBar';
import { LeadConvertDialog } from './components/LeadConvertDialog';
import { LeadCreateDialog } from './components/LeadCreateDialog';
import { LeadStatusChip, statusColor } from './components/LeadStatusChip';
import { formatLeadSource, formatLeadStatus } from '@/lib/format';
import { t } from '@/i18n';

const DEFAULT_PAGE_SIZE = 20;

/** Порядок статусов в чипах фильтра (список лидов «свежие первыми»: NEW → IN_PROGRESS → CONVERTED → DISCARDED). */
const LEAD_STATUS_ORDER: LeadStatus[] = ['NEW', 'IN_PROGRESS', 'CONVERTED', 'DISCARDED'];

/** Флажки полей поиска лидов (SSOT §3.3; заголовок title — только в дефолтном поиске «все поля»). */
function buildLeadFieldOptions(): SearchFieldOption[] {
  return [
    { key: 'first_name', label: t('search.field.first_name') },
    { key: 'last_name', label: t('search.field.last_name') },
    { key: 'email', label: t('search.field.email') },
    { key: 'phone', label: t('search.field.phone') },
    { key: 'company_name', label: t('search.field.company_name') },
    { key: 'license_number', label: t('search.field.license_number') },
    { key: 'site_name', label: t('search.field.site_name') },
    { key: 'ip_address', label: t('search.field.ip_address') },
  ];
}

/** Отображаемое имя лида: «ФИО» или заголовок (канон «ФИО/Компания»). */
function leadDisplayName(lead: Lead): string {
  return `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim() || lead.title || '—';
}

/** Страница списка лидов. */
export function LeadsListPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [searchValue, setSearchValue] = useState<SearchFieldValue>({ q: '', fields: [] });
  const [status, setStatus] = useState<LeadStatus | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [convertLead, setConvertLead] = useState<Lead | null>(null);

  const query = useQuery({
    queryKey: ['leads', page, searchValue.q, searchValue.fields.join(','), status],
    queryFn: () =>
      leadsApi.list({
        page,
        size: DEFAULT_PAGE_SIZE,
        ...(searchValue.q ? { q: searchValue.q } : {}),
        ...(searchValue.fields.length > 0 ? { fields: searchValue.fields.join(',') } : {}),
        ...(status ? { status } : {}),
      }),
  });

  /** Смена поиска (q после debounce / поля сразу) — сброс пагинации. */
  const handleSearchChange = (next: SearchFieldValue): void => {
    setPage(0);
    setSearchValue(next);
  };

  /** Клик по чипу статуса: выбрать/снять (одиночный фильтр — контракт `status=`). */
  const handleStatusToggle = (next: LeadStatus): void => {
    setPage(0);
    setStatus((prev) => (prev === next ? null : next));
  };

  /** Успех конверсии: инвалидация лидов + навигация к сделке (приёмочный критерий №4). */
  const handleConverted = (deal: Deal): void => {
    void queryClient.invalidateQueries({ queryKey: ['leads'] });
    navigate(`/crm/deals/${deal.id}`);
  };

  return (
    <Box>
      <PageHeader title={t('leads.title')} subtitle={t('leads.subtitle')} />
      <Stack spacing={2} sx={{ mb: 2 }}>
        <SearchFieldBar
          fieldsOptions={buildLeadFieldOptions()}
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('leads.search')}
          searchAriaLabel={t('leads.search')}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            Статус:
          </Typography>
          {LEAD_STATUS_ORDER.map((s) => {
            const selected = status === s;
            return (
              <Chip
                key={s}
                data-testid={`lead-status-${s}`}
                label={formatLeadStatus(s, t)}
                clickable
                onClick={() => handleStatusToggle(s)}
                color={selected ? statusColor(s) : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                size="small"
              />
            );
          })}
        </Stack>
      </Stack>
      {query.isLoading ? (
        <LoadingSpinner label={t('leads.loading')} />
      ) : query.isError ? (
        <EmptyState
          title={t('leads.loadError')}
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : !query.data || query.data.content.length === 0 ? (
        <EmptyState
          title={t('leads.empty')}
          description={t('leads.emptyDesc')}
          actionLabel={t('leads.create')}
          onAction={() => setCreateOpen(true)}
        />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Имя</TableCell>
                  <TableCell>Компания</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell>Лицензия</TableCell>
                  <TableCell>Сайт</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Источник</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.content.map((lead: Lead) => (
                  <TableRow
                    key={lead.id}
                    hover
                    onClick={() => navigate(`/crm/leads/${lead.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {leadDisplayName(lead)}
                      </Typography>
                    </TableCell>
                    <TableCell>{lead.companyName ?? '—'}</TableCell>
                    <TableCell>{lead.email ?? '—'}</TableCell>
                    <TableCell>{lead.phone ?? '—'}</TableCell>
                    <TableCell>{lead.licenseNumber ?? '—'}</TableCell>
                    <TableCell>{lead.siteName ?? '—'}</TableCell>
                    <TableCell>{lead.ipAddress ?? '—'}</TableCell>
                    <TableCell>{formatLeadSource(lead.source, t)}</TableCell>
                    <TableCell>
                      <LeadStatusChip status={lead.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('leads.convert.submit')}>
                        <IconButton
                          size="small"
                          aria-label={t('leads.convert.submit')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConvertLead(lead);
                          }}
                        >
                          <TrendingUpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={query.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Paper>
      )}
      <Stack direction="row" justifyContent="flex-end">
        <Fab
          color="primary"
          aria-label={t('leads.create')}
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
          onClick={() => setCreateOpen(true)}
        >
          <AddIcon />
        </Fab>
      </Stack>
      {convertLead ? (
        <LeadConvertDialog
          open={Boolean(convertLead)}
          lead={convertLead}
          onClose={() => setConvertLead(null)}
          onConverted={handleConverted}
        />
      ) : null}
      <LeadCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  );
}
